import styled from 'styled-components';
import { PhoneIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { formatPhoneWithDashes } from '@rsos/utils';
import { DateInformation } from './DateInformation';
import { HeaderNotifications } from './HeaderNotifications/HeaderNotifications';
import { LocationInformation } from './LocationInformation';

interface Props {
  isInternational: boolean;
  callerID: string;
  timestamp: number;
  recentTranscriptionsCount?: number;
  onCallerIdClick?: () => void;
}

const StyledCallerIdMain = styled.span<{ $isClickable?: boolean }>`
  ${({ $isClickable }) =>
    $isClickable
      ? `
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-color: currentColor;
    text-underline-offset: 2px;
  `
      : ''}
`;

const StyledRecentCount = styled.span`
  margin-left: 4px;
  line-height: inherit;
  text-decoration: none;
`;

const StyledCollapsedCallerRow = styled(Box)<{ $isClickable?: boolean }>`
  ${({ $isClickable }) =>
    $isClickable
      ? `
    cursor: pointer;

    &:hover ${StyledCallerIdMain},
    &:focus-within ${StyledCallerIdMain} {
      text-decoration-style: solid;
    }
  `
      : ''}
`;

const StyledCollapsedCallerID = styled(Text)<{ $isClickable?: boolean }>`
  display: inline-flex;
  align-items: baseline;
  text-decoration: none;
`;

export const CollapsedHeaderData = ({
  callerID,
  isInternational,
  timestamp,
  recentTranscriptionsCount = 0,
  onCallerIdClick,
}: Props) => {
  const canOpenRecentTranscripts =
    recentTranscriptionsCount > 0 && typeof onCallerIdClick === 'function';

  return (
    <Box>
      <StyledCollapsedCallerRow
        horizontal
        align="center"
        $isClickable={canOpenRecentTranscripts}
        role={canOpenRecentTranscripts ? 'button' : undefined}
        tabIndex={canOpenRecentTranscripts ? 0 : undefined}
        aria-label={
          canOpenRecentTranscripts
            ? `Open recent transcripts (${recentTranscriptionsCount})`
            : undefined
        }
        onClick={canOpenRecentTranscripts ? onCallerIdClick : undefined}
        onKeyDown={
          canOpenRecentTranscripts
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCallerIdClick?.();
                }
              }
            : undefined
        }
      >
        <PhoneIcon
          color="panePrimaryText"
          viewboxHeight={16}
          viewboxWidth={14}
          width={12}
        />
        <StyledCollapsedCallerID
          $isClickable={canOpenRecentTranscripts}
          color="panePrimaryText"
          fontWeight="bold"
          lineHeight="normal"
          marginLeft={2}
        >
          <StyledCallerIdMain $isClickable={canOpenRecentTranscripts}>
            {isInternational ? callerID : formatPhoneWithDashes(callerID)}
          </StyledCallerIdMain>
          {canOpenRecentTranscripts && (
            <StyledRecentCount>({recentTranscriptionsCount})</StyledRecentCount>
          )}
        </StyledCollapsedCallerID>
      </StyledCollapsedCallerRow>
      <DateInformation timestamp={timestamp} />
      <LocationInformation />
      <HeaderNotifications callerID={callerID} />
    </Box>
  );
};
